import { LitElement, css, html, nothing } from 'lit';
import qrcode from 'qrcode-generator';
import { NICKNAME_WORDS, randomNickname, type KidProfile } from '@ka/contracts';
import { shared, spiderSvg } from '@ka/ui';
import {
  AuthError,
  addPasskey,
  confirmSignUp,
  deleteFamilyAccount,
  familyApi,
  forgotPassword,
  isDev,
  isSignedIn,
  passkeysSupported,
  resendSignUpCode,
  resetPassword,
  signInWithPasskey,
  signInWithPassword,
  signOut,
  signUp,
  storedInvite,
  submitEmailCode,
  type Step,
} from './family-auth.ts';
import { currentKid, pairDevice, unpairDevice } from './kid.ts';
import { navigate } from './router.ts';

const formStyles = css`
  :host {
    max-width: 34rem;
  }
  form {
    display: grid;
    gap: 0.9rem;
    margin-top: 1rem;
  }
  label {
    display: grid;
    gap: 0.35rem;
    font-weight: 600;
  }
  input,
  select {
    /* Fill the column without overflowing it (big letter-spaced codes on phones). */
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    font: inherit;
    min-height: 48px;
    padding: 0 0.75rem;
    background: var(--ka-stone-900);
    color: var(--ka-parchment);
    border: 2px solid var(--ka-stone-400);
  }
  input[type='checkbox'] {
    width: 24px;
    height: 24px;
    min-height: auto;
    margin: 0;
    flex: none;
  }
  /* A checkbox sits beside its words, and the whole line is the tap target. */
  label:has(> input[type='checkbox']) {
    display: flex;
    align-items: flex-start;
    flex-wrap: nowrap;
    gap: 0.6rem;
    min-height: 44px;
  }
  .link {
    all: unset;
    cursor: pointer;
    color: var(--ka-gold);
    text-decoration: underline;
    min-height: 44px;
    display: inline-flex;
    align-items: center;
  }
  .link:focus-visible {
    outline: 3px solid var(--ka-focus);
  }
  .err {
    color: var(--ka-red-bright);
  }
  .row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.6rem;
    align-items: center;
  }
`;

const message = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong.');

type AuthMode = 'signIn' | 'password' | 'emailCode' | 'signUp' | 'confirm' | 'forgot' | 'reset';

/** Parent sign-in and sign-up on our own page (families pool, no Cognito redirect). */
export class KaParentAuth extends LitElement {
  static override properties = {
    start: {},
    mode: { state: true },
    busy: { state: true },
    error: { state: true },
    note: { state: true },
  };
  declare start: 'signIn' | 'signUp';
  declare mode: AuthMode;
  declare busy: boolean;
  declare error: string;
  declare note: string;
  private email = '';
  private password = '';
  private step: Exclude<Step, { kind: 'done' }> | null = null;

  static override styles = [shared, formStyles];

  constructor() {
    super();
    this.start = 'signIn';
    this.busy = false;
    this.error = '';
    this.note = '';
  }

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has('start')) this.mode = this.start;
  }

  private value(name: string) {
    return this.renderRoot.querySelector<HTMLInputElement>(`input[name="${name}"]`)?.value ?? '';
  }

  private async run(fn: () => Promise<Step | AuthMode>) {
    this.busy = true;
    this.error = '';
    this.note = '';
    try {
      const r = await fn();
      if (typeof r === 'string') this.mode = r;
      else if (r.kind === 'done') {
        this.password = '';
        navigate('/parents/family');
        return;
      } else {
        this.step = r;
        this.mode = 'emailCode';
      }
      await this.updateComplete;
      this.renderRoot.querySelector<HTMLInputElement>('input:not([disabled])')?.focus();
    } catch (e) {
      if (e instanceof AuthError && e.code === 'NoPasskey') this.mode = 'password';
      if (e instanceof AuthError && e.code === 'UserNotConfirmedException') {
        await resendSignUpCode(this.email).catch(() => undefined);
        this.mode = 'confirm';
        this.note = 'Please confirm your email first. We sent you a new code.';
      } else this.error = message(e);
    } finally {
      this.busy = false;
    }
  }

  private submit(e: Event) {
    e.preventDefault();
    if (this.busy) return;
    const email = this.value('email').trim();
    if (email) this.email = email;
    switch (this.mode) {
      case 'signIn':
        return void this.run(() => signInWithPasskey(this.email));
      case 'password':
        this.password = this.value('password');
        return void this.run(() => signInWithPassword(this.email, this.password));
      case 'emailCode':
        return void this.run(() => submitEmailCode(this.step!, this.value('code')));
      case 'signUp':
        if (!(this.renderRoot.querySelector('input[name="consent"]') as HTMLInputElement).checked) {
          this.error = 'Please confirm you are the parent or guardian.';
          return;
        }
        this.password = this.value('password');
        if (!this.value('invite').trim()) {
          this.error = 'Please enter the invite code from your card.';
          return;
        }
        return void this.run(async () => {
          await signUp(this.email, this.password, this.value('invite'));
          this.note = `We emailed a code to ${this.email}.`;
          return 'confirm';
        });
      case 'confirm':
        return void this.run(async () => {
          await confirmSignUp(this.email, this.value('code'));
          if (!this.password) return 'password';
          return signInWithPassword(this.email, this.password);
        });
      case 'forgot':
        return void this.run(async () => {
          await forgotPassword(this.email);
          this.note = `If ${this.email} has an account, we emailed it a code.`;
          return 'reset';
        });
      case 'reset':
        return void this.run(async () => {
          this.password = this.value('password');
          await resetPassword(this.email, this.value('code'), this.password);
          return signInWithPassword(this.email, this.password);
        });
    }
  }

  private go(mode: AuthMode) {
    return () => ((this.mode = mode), (this.error = ''), (this.note = ''));
  }

  private fields() {
    const email = html`<label
      >Your email
      <input
        name="email"
        type="email"
        autocomplete="username webauthn"
        required
        .value=${this.email}
        ?disabled=${!['signIn', 'password', 'signUp', 'forgot'].includes(this.mode)}
    /></label>`;
    const newPassword = html`<label
      >Password
      <span class="hint" id="password-hint">At least 12 characters, with a number</span>
      <input
        name="password"
        type="password"
        autocomplete="new-password"
        minlength="12"
        required
        aria-describedby="password-hint"
    /></label>`;
    const code = html`<label
      >Code from the email
      <input name="code" inputmode="numeric" maxlength="8" autocomplete="one-time-code" required
    /></label>`;
    const btn = (label: string) =>
      html`<button class="btn primary" ?disabled=${this.busy}>${label}</button>`;
    switch (this.mode) {
      case 'signIn':
        return html`${email} ${btn(passkeysSupported() ? 'Sign in with passkey' : 'Continue')}
          <button type="button" class="link" @click=${this.go('password')}>
            Use password instead
          </button>
          <button type="button" class="link" @click=${this.go('signUp')}>
            New here? Create a family account
          </button>`;
      case 'password':
        return html`${email}
          <label
            >Password
            <input name="password" type="password" autocomplete="current-password" required
          /></label>
          ${btn('Sign in')}
          <button type="button" class="link" @click=${this.go('forgot')}>Forgot password?</button>
          <button type="button" class="link" @click=${this.go('signIn')}>
            Use a passkey instead
          </button>`;
      case 'emailCode':
        return html`<p>We emailed you a sign-in code.</p>
          ${code} ${btn('Sign in')}`;
      case 'signUp':
        return html`<label
            >Invite code (on the card)
            <input
              name="invite"
              .value=${storedInvite()}
              maxlength="9"
              autocomplete="off"
              autocapitalize="characters"
              spellcheck="false"
              required
          /></label>
          ${email} ${newPassword}
          <label class="row"
            ><input name="consent" type="checkbox" required />
            <span
              >I am a parent or guardian, and I have read the
              <a href="/parents#privacy">privacy notice</a>.</span
            ></label
          >
          ${btn('Create family account')}
          <button type="button" class="link" @click=${this.go('signIn')}>
            Already have an account? Sign in
          </button>`;
      case 'confirm':
        return html`${email} ${code} ${btn('Confirm email')}
          <button
            type="button"
            class="link"
            @click=${() =>
              this.run(async () => {
                await resendSignUpCode(this.email);
                return 'confirm';
              })}
          >
            Send a new code
          </button>`;
      case 'forgot':
        return html`${email} ${btn('Email me a reset code')}`;
      case 'reset':
        return html`${email} ${code} ${newPassword} ${btn('Set new password')}`;
    }
  }

  override render() {
    const title =
      this.mode === 'signUp' || this.mode === 'confirm'
        ? 'Create a family account'
        : 'Parent sign-in';
    return html`<section class="panel">
      <h1>${title}</h1>
      <p class="muted">For parents and guardians. Kids never need an email or password.</p>
      ${
        isDev
          ? html`<p>
              <span class="tag fixture">LOCAL DEV</span> No real sign-in here.
              <a href="/parents/family">Open the test family</a>.
            </p>`
          : nothing
      }
      <form @submit=${this.submit} novalidate>
        ${this.fields()} ${this.note ? html`<p role="status">${this.note}</p>` : nothing}
        ${this.error ? html`<p class="err" role="alert">${this.error}</p>` : nothing}
        ${this.busy ? html`<p role="status" class="muted">One moment…</p>` : nothing}
      </form>
    </section>`;
  }
}
customElements.define('ka-parent-auth', KaParentAuth);

interface Pairing {
  kidId: string;
  code: string;
  expiresAt: string;
  qr: string;
}

/** The signed-in parent's page: kid profiles, pairing codes, devices, account removal. */
export class KaFamily extends LitElement {
  static override properties = {
    kids: { state: true },
    suggestions: { state: true },
    pairing: { state: true },
    busy: { state: true },
    msg: { state: true },
  };
  declare kids: KidProfile[] | null;
  declare suggestions: string[];
  declare pairing: Pairing | null;
  declare busy: boolean;
  declare msg: string;

  static override styles = [
    shared,
    formStyles,
    css`
      :host {
        max-width: 48rem;
        display: grid;
        gap: 1.5rem;
      }
      ul {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        gap: 1rem;
      }
      .code {
        font: 600 2rem var(--ka-font-display);
        letter-spacing: 0.2em;
        color: var(--ka-gold);
      }
      img.qr {
        image-rendering: pixelated;
        background: #fff;
        width: 200px;
        height: 200px;
      }
    `,
  ];

  constructor() {
    super();
    this.kids = null;
    this.suggestions = [];
    this.pairing = null;
    this.busy = false;
    this.msg = '';
  }

  override connectedCallback() {
    super.connectedCallback();
    if (!isSignedIn()) {
      navigate('/parents/sign-in', true);
      return;
    }
    this.reroll();
    void this.load();
  }

  private reroll() {
    this.suggestions = Array.from({ length: 4 }, randomNickname);
  }

  private async load() {
    try {
      this.kids = await familyApi<KidProfile[]>('GET', '/kids');
    } catch (e) {
      this.msg = message(e);
    }
  }

  private async act(fn: () => Promise<unknown>, done = '') {
    this.busy = true;
    this.msg = '';
    try {
      await fn();
      this.msg = done;
      await this.load();
    } catch (e) {
      this.msg = message(e);
    } finally {
      this.busy = false;
    }
  }

  private addKid(e: Event) {
    e.preventDefault();
    const nickname = new FormData(e.target as HTMLFormElement).get('nickname') as string | null;
    if (!nickname) return;
    void this.act(async () => {
      await familyApi('POST', '/kids', { nickname });
      this.reroll();
    }, `${nickname} was added. Now pair their device.`);
  }

  private pair(k: KidProfile) {
    void this.act(async () => {
      const r = await familyApi<{ code: string; expiresAt: string }>(
        'POST',
        `/kids/${k.id}/pairing-code`,
      );
      const qr = qrcode(0, 'M');
      qr.addData(`${location.origin}/pair?code=${r.code}`);
      qr.make();
      this.pairing = { kidId: k.id, ...r, qr: qr.createDataURL(6, 2) };
    });
  }

  private kidRow(k: KidProfile) {
    const p = this.pairing?.kidId === k.id ? this.pairing : null;
    const found = k.progress?.found.length ?? 0;
    return html`<li class="panel">
      <h3>${spiderSvg(18)} ${k.nickname}</h3>
      <p class="muted">Spiders found: ${found}</p>
      ${
        p
          ? html`<div>
              <p>
                On your kid’s device, scan this code or open
                <strong>${location.host}/pair</strong> and type:
              </p>
              <p class="code" aria-label=${`Pairing code ${p.code.split('').join(' ')}`}>
                ${p.code}
              </p>
              <img
                class="qr"
                src=${p.qr}
                alt="QR code that opens the pairing page with this code"
              />
              <p class="muted">
                Works once, until
                ${new Date(p.expiresAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}.
              </p>
            </div>`
          : nothing
      }
      <div class="row">
        <button class="btn green" ?disabled=${this.busy} @click=${() => this.pair(k)}>
          Pair a device
        </button>
        <button
          class="btn"
          ?disabled=${this.busy}
          @click=${() =>
            confirm(`Sign ${k.nickname} out of every paired device?`) &&
            this.act(
              () => familyApi('DELETE', `/kids/${k.id}/sessions`),
              'All devices signed out.',
            )}
        >
          Sign out all devices
        </button>
        <button
          class="btn"
          ?disabled=${this.busy}
          @click=${() =>
            confirm(`Remove ${k.nickname}? Their progress is deleted.`) &&
            this.act(() => familyApi('DELETE', `/kids/${k.id}`), `${k.nickname} was removed.`)}
        >
          Remove
        </button>
      </div>
    </li>`;
  }

  override render() {
    return html`<section class="panel">
        <h1>Your family</h1>
        ${isDev ? html`<p><span class="tag fixture">LOCAL DEV</span> Test family, no real account.</p>` : nothing}
        <p>Kids get a nickname only — no real names, emails or passwords.</p>
        ${this.msg ? html`<p role="status">${this.msg}</p>` : nothing}
      </section>
      <section>
        ${
          this.kids === null
            ? html`<p class="muted">Loading…</p>`
            : this.kids.length
              ? html`<ul>
                  ${this.kids.map((k) => this.kidRow(k))}
                </ul>`
              : html`<p class="muted">No kid profiles yet.</p>`
        }
      </section>
      <section class="panel">
        <h2>Add a kid</h2>
        <form @submit=${this.addKid}>
          <label
            >Nickname
            <select name="nickname" required>
              ${this.suggestions.map((n) => html`<option>${n}</option>`)}
            </select></label
          >
          <div class="row">
            <button class="btn primary" ?disabled=${this.busy}>Add</button>
            <button type="button" class="link" @click=${this.reroll}>Show other nicknames</button>
          </div>
          <p class="muted">
            Nicknames are made from a fixed word list (${NICKNAME_WORDS.first[0]},
            ${NICKNAME_WORDS.second[0]}…) so they never contain real names.
          </p>
        </form>
      </section>
      <section class="panel">
        <h2>Your account</h2>
        <div class="row">
          ${
            !isDev && passkeysSupported()
              ? html`<button
                  class="btn"
                  ?disabled=${this.busy}
                  @click=${() => this.act(addPasskey, 'Passkey added. Next time, sign in with it.')}
                >
                  Add a passkey for this device
                </button>`
              : nothing
          }
          <button class="btn" @click=${() => void signOut().then(() => navigate('/parents'))}>
            Sign out
          </button>
          <button
            class="btn"
            ?disabled=${this.busy}
            @click=${() =>
              confirm('Delete your family account and every kid profile? This cannot be undone.') &&
              this.act(async () => {
                await deleteFamilyAccount();
                navigate('/parents');
              })}
          >
            Delete family account
          </button>
        </div>
      </section>`;
  }
}
customElements.define('ka-family', KaFamily);

/** The kid's side: type (or scan) the code a parent made. */
export class KaPair extends LitElement {
  static override properties = {
    version: { type: Number },
    code: {},
    paired: { state: true },
    busy: { state: true },
    error: { state: true },
  };
  declare version: number;
  declare code: string;
  declare paired: KidProfile | null;
  declare busy: boolean;
  declare error: string;

  static override styles = [
    shared,
    formStyles,
    css`
      input[name='code'] {
        font: 600 1.6rem var(--ka-font-display);
        letter-spacing: 0.2em;
        text-transform: uppercase;
      }
    `,
  ];

  constructor() {
    super();
    this.version = 1;
    this.code = '';
    this.paired = currentKid();
    this.busy = false;
    this.error = '';
  }

  private submit(e: Event) {
    e.preventDefault();
    const code = this.renderRoot.querySelector<HTMLInputElement>('input[name="code"]')!.value;
    this.busy = true;
    this.error = '';
    pairDevice(code, this.version)
      .then((k) => (this.paired = k))
      .catch((err) => (this.error = message(err)))
      .finally(() => (this.busy = false));
  }

  override render() {
    if (this.paired)
      return html`<section class="panel">
        <h1>${spiderSvg(28)} Hi, ${this.paired.nickname}!</h1>
        <p>This device is paired. Spiders you find are saved to your profile.</p>
        <p class="row">
          <a class="btn green" href="/lore">Go spider hunting</a>
          <button
            class="btn"
            @click=${() =>
              confirm('Unpair this device?') &&
              void unpairDevice().then(() => (this.paired = null))}
          >
            Unpair this device
          </button>
        </p>
      </section>`;
    return html`<section class="panel">
      <h1>${spiderSvg(28)} Pair this device</h1>
      <p>Ask your grown-up for the code on their screen.</p>
      <form @submit=${this.submit}>
        <label
          >Code
          <input
            name="code"
            .value=${this.code}
            maxlength="9"
            autocomplete="off"
            autocapitalize="characters"
            spellcheck="false"
            required
        /></label>
        <button class="btn primary" ?disabled=${this.busy}>Pair</button>
        ${this.error ? html`<p class="err" role="alert">${this.error}</p>` : nothing}
      </form>
    </section>`;
  }
}
customElements.define('ka-pair', KaPair);
